/**
 * Secure Cookie Service
 * 
 * This service handles secure cookie operations, particularly for httpOnly cookies
 * that store JWT tokens. It provides a bridge between frontend and backend
 * for authentication token management.
 */

class SecureCookieService {
    /**
     * Check if JWT token exists (by making a test request to server)
     * Since httpOnly cookies can't be read by JS, we need server validation
     * @returns {Promise<boolean>} Whether valid JWT exists
     */
    async hasValidJWT() {
        try {
            const response = await fetch('/api/auth/verify', {
                method: 'GET',
                credentials: 'include', // Include httpOnly cookies
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            return response.ok;
        } catch (error) {
            console.error('Error checking JWT validity:', error);
            return false;
        }
    }

    /**
     * Login and set httpOnly JWT cookie
     * @param {string} username - Username
     * @param {string} password - Password
     * @param {string} organizationName - Organization name
     * @returns {Promise<Object>} Login response
     */
    async login(username, password, organizationName) {
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                credentials: 'include', // Allow setting httpOnly cookies
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    operation: 'login',
                    requestBody: {
                        username,
                        password,
                        organizationName
                    }
                })
            });

            const data = await response.json();
            
            if (response.ok && data.status === 'SUCCESS') {
                // JWT is now stored in httpOnly cookie by server
                // Return user data for frontend storage
                return {
                    success: true,
                    userData: data.body
                };
            } else {
                return {
                    success: false,
                    error: data.message || 'Login failed'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: 'Network error during login'
            };
        }
    }

    /**
     * Logout and clear httpOnly JWT cookie
     * @returns {Promise<boolean>} Success status
     */
    async logout() {
        try {
            const response = await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include', // Include cookies for logout
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            return response.ok;
        } catch (error) {
            console.error('Logout error:', error);
            return false;
        }
    }

    /**
     * Make authenticated request (JWT automatically included)
     * @param {string} url - Request URL
     * @param {Object} options - Fetch options
     * @returns {Promise<Response>} Fetch response
     */
    async authenticatedFetch(url, options = {}) {
        const defaultOptions = {
            credentials: 'include', // Always include httpOnly cookies
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        return fetch(url, { ...defaultOptions, ...options });
    }

    /**
     * Get CSRF token if needed (for CSRF protection)
     * @returns {Promise<string|null>} CSRF token
     */
    async getCSRFToken() {
        try {
            const response = await fetch('/api/auth/csrf-token', {
                method: 'GET',
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                return data.csrfToken;
            }
            
            return null;
        } catch (error) {
            console.error('Error getting CSRF token:', error);
            return null;
        }
    }

    /**
     * Refresh JWT token (if server supports refresh)
     * @returns {Promise<boolean>} Success status
     */
    async refreshToken() {
        try {
            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                credentials: 'include'
            });

            return response.ok;
        } catch (error) {
            console.error('Token refresh error:', error);
            return false;
        }
    }
}

// Create singleton instance
const secureCookieService = new SecureCookieService();

export { SecureCookieService };
export default secureCookieService;
